<template>
  <VDialogV3
    position="center"
    title="Импорт"
    :entity="assembly"
    :loading="false"
  >
    <div class="import-data-wrap">
      <div
        v-if="!showBuyersCode && !showOrderPositions"
        class="import-header"
      >
        <VInput
          class="import-input"
          placeholder="Ctrl + V сюда выделенный диапазон (вставьте список позиций)"
          @paste="onImport($event)"
        />

        <div
          v-if="importErrorsText"
          class="import-errors"
        >
          {{ importErrorsText }}
        </div>
      </div>

      <div
        v-if="importedRawData.length > 0"
        class="body"
      >
        <table v-if="importedRawData.length > 0">
          <thead>
          <tr>
            <th
              v-for="(_select, index) of importedRawData[0]"
              :key="uid + '_order_select_' + index"
            >
              <VSelect
                :options="importColumnList"
                v-model="importColumnOrder[index]"
                @update:modelValue="onImportColumnOrderChange($event, index)"
              />
            </th>
          </tr>
          </thead>

          <tbody>
          <tr
            v-for="(row, index) in importedRawData"
            :key="uid + '_row_' + index"
          >
            <td
              v-for="(column, cIndex) of importedRawData[index]"
              :key="uid + '_row_column_' + cIndex"
            >
              {{ column ? column : '--' }}
            </td>
          </tr>
          </tbody>
        </table>
        <div
          v-else
          style="padding: 32px 0 16px 0; text-align: center"
        >
          Нет значений
        </div>
      </div>

      <template v-if="showBuyersCode">
        <VDataGridV2
          ref="buyersCode"
          key="buyersCode"
          class="preview-table"
          :data="indexedPreviewTable"
          sticky
        >
          <VDataGridColumnV2
            id="index"
            label="#"
          >
            <template #default="{ row }">
              {{ row.index + 1 }}
            </template>
          </VDataGridColumnV2>

          <VDataGridColumnV2
            id="filter"
            label="Артикул"
          >
            <template #default="{ row }">
              {{ row.data.buyersCode }}
            </template>
          </VDataGridColumnV2>

          <VDataGridColumnV2
            id="name"
            label="Наименование"
          >
            <template #default="{ row }">
              {{ row.data.name }}
            </template>
          </VDataGridColumnV2>

          <VDataGridColumnV2
            id="qty"
            label="Количество"
          >
            <template #default="{ row }">
              <VInputNumberRus v-model="row.data.qty"/>
            </template>
          </VDataGridColumnV2>

          <VDataGridColumnV2
            id="filter-info"
            label="Товар"
          >
            <template #default="{ row }">
              <div class="item-filter-info">
                <template v-if="row.meta">
                  <template v-if="row.meta.positionIsFound">
                    Позиция с таким артикулом найдена
                  </template>
                  <template v-else>
                    <span class="warning"> Товар по артикулу не найден </span>
                  </template>
                </template>
              </div>
            </template>
          </VDataGridColumnV2>

          <VDataGridColumnV2
            id="action"
            width="50px"
          >
            <template #default="{ row }">
              <VButton
                class="action"
                title="Удалить"
                @click="deleteRow(row)"
              >
                <template #icon>
                  <VIcon material="delete"/>
                </template>
              </VButton>
            </template>
          </VDataGridColumnV2>
        </VDataGridV2>
      </template>

      <template v-if="showOrderPositions">
        <VDataGridV2
          ref="orderPositions"
          key="orderPositions"
          class="preview-table"
          :data="groupedOrderPositions"
          group-children-property="positions"
          sticky
          selectable
          v-model:selected-rows="localSelectedOrderPositions"
        >
          <VDataGridGroupV2>
            <template #default="{ parent }">
              <div class="group">
                <div>Артикул: {{ parent.buyersCode }}</div>
                |
                <div>{{ parent.name }}</div>
                |
                <div>Количество: {{ parent.qty ? parent.qty : '--' }}</div>
              </div>
            </template>
          </VDataGridGroupV2>

          <VDataGridColumnV2
            id="id"
            content-align="left"
            label="№"
            width="4%"
          >
            <template #default="{ row }">
              {{ $core.utils.string.formatWithDelimiter(row.id) }}

              <div class="mds-badges">
                <div
                  v-if="!row.isOfferBased"
                  class="mds-badge primary nowrap"
                >
                  Прямой
                </div>
                <span
                  v-if="row.isDeleted"
                  class="mds-badge warning"
                >Удалён</span>
              </div>
            </template>
          </VDataGridColumnV2>

          <VDataGridColumnV2
            id="name"
            label="Товар"
          >
            <template #label>
              Товар
              <div class="description">Производитель / материал</div>
            </template>
            <template #default="{ row }">
              <span class="ellipsis nowrap" :title="row.name">
                {{ row.name }}
              </span>
              <div class="description">
                <template v-if="isPrintingPosition(row) && row.material">
                  <div class="nowrap ellipsis" :title="row.material">
                    Материал: {{ row.material }}
                  </div>
                </template>
                <template v-if="isRawMaterialsAndPackage(row) && row.manufacturer">
                  <div class="nowrap ellipsis" :title="row.manufacturer.name">
                    Производитель: {{ row.manufacturer.name }}
                  </div>
                </template>
              </div>
            </template>
          </VDataGridColumnV2>

          <VDataGridColumnV2
            id="qty"
            label="Количество"
            width="8%"
          >
            <template #label>
              <div class="nowrap">Кол-во</div>
              <div class="description">Осталось</div>
            </template>
            <template #default="{ row }">
              <div class="nowrap">
                {{ row.qty.format(row.unit) }}
              </div>
              <div
                v-if="getPositionQtyRemainder(row).isPositive()"
                class="nowrap description"
              >
                {{ getPositionQtyRemainder(row).format(row.unit) }}
              </div>
            </template>
          </VDataGridColumnV2>

          <VDataGridColumnV2
            id="priceFullSeller"
            content-align="right"
            width="9%"
          >
            <template #label>
              <div class="nowrap">Цена товара</div>
              <div class="description">Сумма</div>
            </template>
            <template #default="{ row }">
              <div>
                <div class="nowrap">
                  {{ row.priceFullSeller.format(row.currencyCode, row.unit) }}
                </div>
                <div class="description">
                  {{ row.sumFullSeller.format(row.currencyCode, row.unit.name) }}
                </div>
              </div>
            </template>
          </VDataGridColumnV2>

          <VDataGridColumnV2
            id="desiredReceiptDate"
            width="7%"
          >
            <template #label>
              <div class="nowrap">Дата доставки</div>
            </template>
            <template #default="{ row }">
              <div class="nowrap">
                {{ $core.utils.date.humanizeDate(row.desiredReceiptDate, null) }}
              </div>
            </template>
          </VDataGridColumnV2>

          <VDataGridColumnV2
            id="plannedShipmentDate"
            width="240px"
          >
            <template #label>
              <div>Дата отгрузки</div>
              <div class="description">Тип доставки</div>
            </template>
            <template #default="{ row }">
              <div class="nowrap">
                {{ $core.utils.date.humanizeDate(row.plannedShipmentDate, null) }}
              </div>
              <div class="description">
                {{ row.deliveryType.name }}
              </div>
            </template>
          </VDataGridColumnV2>
        </VDataGridV2>
      </template>
    </div>

    <template #footer>
      <template v-if="!hasPreviewTable">
        <VButton
          :disabled="!canApproveImport"
          theme="primary"
          @click="approveImport"
        >
          Применить
        </VButton>
      </template>
      <div
        v-if="hasPreviewTable && !showOrderPositions"
        class="button-wrapper"
      >
        <VButton
          :disabled="canDoImport"
          theme="primary"
          @click="doImport"
        >
          Сохранить
        </VButton>
        <span
          v-if="canDoImport"
          class="warning-wrapper"
        >
          <VIcon material="warning_amber"/>
          {{ qtyUnresolvedPositions }}
          {{ $core.utils.regional.ru.pluralize(qtyUnresolvedPositions, 'позиция', 'позиции', 'позиций') }}
          не найдено
        </span>
        <VButton
          v-if="canDoImport"
          class="warning-clean-btn"
          @click="deleteNotFoundPositions"
        >
          Удалить не найденные позиции
        </VButton>
      </div>
      <div v-if="showOrderPositions">
        <VButton
          theme="primary"
          @click="onComplete"
        >
          Завершить
        </VButton>
      </div>
      <div/>
      <VButton
        class="close"
        @click="close"
      >
        Назад
      </VButton>
    </template>
  </VDialogV3>
</template>

<style lang="scss" src="./AssemblyPositionImportDialog.scss"/>

<script lang="ts">
import ClipboardXlsParserUtils from '@/core/utils/ClipboardXlsParserUtils';
import ValidationMixin         from '@/mixins/validation-mixin';
import UniqueIdMixin           from '@/lib/mixins/component-unique-id';
import OrdersService           from "@/domains/Orders/OrdersService";
import Assembly                from "@/domains/Assemblies/Assembly";
import type Qty                from "@/core/types/Qty";

const IMPORT_COLUMN_LIST = [
  {id: 'buyersCode', name: 'Артикул'},
  {id: 'name', name: 'Наименование'},
  {id: 'qty', name: 'Количество'},
];

export default defineComponent({
  name: 'AssemblyPositionImportDialog',

  mixins: [ValidationMixin, UniqueIdMixin],

  props: {
    assembly: {
      type: Object as PropType<Assembly>,
      default: null,
    },
  },

  emits: ['close'],

  setup() {
    return {
      importColumnList: IMPORT_COLUMN_LIST,
    };
  },

  data() {
    return {
      importedRawData: [] as any[][],
      importColumnOrder: [...IMPORT_COLUMN_LIST] as Array<{ id: string; name: string } | null>,
      previewTable: null as any[] | null,
      importHelper: null as ClipboardXlsParserUtils | null,
      localSelectedOrderPositions: [] as any[],

      orderBuyersCodes: null as any,
      orderPositions: null as any[] | null,

      showBuyersCode: false,
      showOrderPositions: false,

      form: [] as any[],
    };
  },

  computed: {
    /**
     * previewTable с проставленным index, используется в первой превью-таблице,
     * чтобы рисовать колонку "#" (нумерация) и удалять строки по reference
     */
    indexedPreviewTable(): any[] {
      if (!Array.isArray(this.previewTable)) return [];
      return this.previewTable.map((item, index) => ({...item, index}));
    },

    /**
     * Сгруппированные данные для второй грид-таблицы — для каждого распознанного
     * импортированного артикула собираем найденные позиции заказа в positions[]
     */
    groupedOrderPositions(): any[] {
      if (!Array.isArray(this.previewTable) || !Array.isArray(this.orderPositions)) {
        return [];
      }

      return this.previewTable
        .filter((pos: any) => pos.meta?.positionIsFound)
        .map(({data}: any) => ({
          id: data.buyersCode,
          buyersCode: data.buyersCode,
          name: data.name,
          qty: data.qty,
          positions: this.orderPositions!.filter(
            orderPosition => Number(orderPosition.buyersCode) === Number(data.buyersCode),
          ),
        }));
    },

    hasPreviewTable(): boolean {
      return this.$core.utils.isNotEmptyArray(this.previewTable);
    },

    qtyUnresolvedPositions(): number | null {
      if (!this.hasPreviewTable) return null;
      return this.previewTable!.filter(({meta}: any) => !meta?.positionIsFound).length;
    },

    canApproveImport(): boolean {
      if (this.importedRawData.length === 0) return false;
      const codeIndex = this.importColumnOrder.findIndex(
        item => item && item.id === 'buyersCode',
      );
      return codeIndex > -1;
    },

    canDoImport(): boolean {
      return Boolean(this.qtyUnresolvedPositions);
    },

    importErrorsText(): string | null {
      if (this.importedRawData.length === 0) return null;
      const codeIndex = this.importColumnOrder.findIndex(
        item => item !== null && item.id === 'buyersCode',
      );
      return codeIndex > -1
        ? 'Можно импортировать данные'
        : 'Нельзя импортировать данные';
    },
  },

  methods: {
    /**
     * Парсит вставленный из буфера обмена диапазон (XLS / текст) в строки импорта
     */
    onImport($event: ClipboardEvent): void {
      if (!($event instanceof ClipboardEvent)) return;

      this.importHelper = new ClipboardXlsParserUtils($event);

      if (this.importHelper.rows.length === 0) {
        const rawData = $event.clipboardData?.getData('text') ?? '';
        const a = [...rawData.matchAll(/(?<code>\d{6})(?<name>[^]+?)(?:-?(?<qty>[\d\s.,]+?)шт)?(?:(?<=шт)[^]*?)?(?=\s*\d{6}|$)/giu)];

        a.forEach(_ => {
          const {code, name, qty} = _.groups as any;
          this.importHelper!.rows.push([code, name, qty]);
        });
      }

      if (this.importHelper.rows.length > 0) {
        this.importedRawData = this.importHelper.rows;
      }
    },

    /**
     * Меняет назначение колонки в импорте,
     * гарантирует, что одна и та же сущность не назначена в двух колонках
     */
    onImportColumnOrderChange(value: any, position: number): void {
      const importColumnOrder = this.$core.utils.cloneDeep(this.importColumnOrder);

      if (value === null) {
        importColumnOrder[position] = null;
      } else {
        const index = importColumnOrder.findIndex(item => item && item.id === value.id);
        if (index !== -1) {
          importColumnOrder[index] = null;
        }
        importColumnOrder[position] = value;
      }
      this.importColumnOrder = importColumnOrder;
    },

    deleteRow(row: any): void {
      if (!Array.isArray(this.previewTable)) return;

      const idx = this.previewTable.indexOf(row) >= 0
        ? this.previewTable.indexOf(row)
        : this.previewTable.findIndex((r: any) => r === row.data);

      if (idx < 0) return;

      const previewTable = [...this.previewTable];
      previewTable.splice(idx, 1);

      delete (this as any).validationState[`form_item_${idx}`];

      this.previewTable = previewTable;
    },

    /**
     * Удалить из превью все строки, для которых не нашлось позиции заказа
     */
    deleteNotFoundPositions(): void {
      if (!Array.isArray(this.previewTable)) return;
      this.previewTable = this.previewTable.filter((pos: any) => pos?.meta?.positionIsFound);
    },

    /**
     * Принять импорт: парсит сырые данные по выбранным колонкам, грузит позиции
     * заказа продавца по списку артикулов и формирует превью-таблицу
     */
    approveImport(): void {
      let form = this.importedRawData.map(item => {
        const result: Record<string, any> = {};
        this.importColumnOrder.forEach((columnOrder, index) => {
          if (columnOrder) {
            result[columnOrder.id] = item[index];
          }
        });
        return result;
      });

      form = form.map(item => {
        const qtyParsed = parseInt(String(item.qty).replaceAll(' ', ''), 10);
        item.qty = isNaN(qtyParsed) ? null : qtyParsed;
        item.buyersCode = item.buyersCode && item.buyersCode.length > 0
          ? item.buyersCode.replaceAll(' ', '')
          : null;
        return item;
      });

      this.form = form;
      this.importedRawData = [];
      this.importColumnOrder = [];

      this.orderPositions = OrdersService.getPositionsListPaginated({
        seller: this.assembly.seller,
        isInWork: true,
        isFinished: false,
        codes: form.filter(item => item !== null).map(item => item.buyersCode),
      })

      this.previewTable = this.form.map((pos: any) => {
        pos.buyersCode = Number(pos.buyersCode);
        const result: any = {
          data: pos,
          meta: { positionIsFound: false },
        };

        if (this.orderPositions!.find((orderPos: any) => Number(orderPos.buyersCode) === Number(pos.buyersCode))) {
          result.meta.positionIsFound = true;
        }
        return result;
      });

      this.showBuyersCode = true;

    },

    /**
     * Переключение со списка артикулов на список найденных позиций заказа
     */
    doImport(): void {
      this.showBuyersCode = false;
      this.showOrderPositions = true;
    },

    /**
     * Завершить импорт — отдаём родителю выбранные пользователем позиции
     */
    onComplete(): void {
      this.localSelectedOrderPositions.forEach((pos) => {
        this.assembly.addPosition(pos);
      })

      this.$emit('close');
    },

    /**
     * Остаток позиции с учётом уже собранного и отгруженного.
     *
     * assembled и shipped — Qty, а не числа: прежняя проверка через isNumber
     * не срабатывала никогда, и метод молча возвращал полное количество.
     * Отсутствующие значения (статуса нет либо поле не заполнено) считаются
     * нулём — вычитать нечего.
     */
    getPositionQtyRemainder(position: any): Qty {
      const { assembled, shipped } = position.shipmentStatus ?? {};
      let qty: Qty = position.qty;

      if (assembled !== undefined && assembled !== null) {
        qty = qty.minus(assembled);
      }

      if (shipped !== undefined && shipped !== null) {
        qty = qty.minus(shipped);
      }

      return qty;
    },

    isPrintingPosition(entity: any): boolean {
      return entity.orderCategory?.id === 'printing';
    },

    isRawMaterialsAndPackage(entity: any): boolean {
      return entity.orderCategory?.id === 'rawMaterialsAndPackage';
    },

    close(): void {
      this.$emit('close');
    },
  },
});
</script>